import { useUpdateEffect } from 'react-use'
import { classNames } from '@g4g/utils/src/react/class-names'
import { FC, useState, useId } from 'react'

/**
 * Number picker with - number + layout, does not allow negative numbers.
 * Consider installing & porting this component to:
 * https://www.npmjs.com/package/use-stepper
 * if negatives are needed. (1.5kb)
 */
const NumberPicker: FC<{
  onChange: (num: number) => void
  buttonClassName: string
  minusButtonClassName?: string
  plusButtonClassName?: string
  inputClassName: string
  className?: string
  disabled?: boolean
  /** Must be greater than or eq to zero. */
  min?: number
  max?: number
  value: number
  placeholder?: string
  /** Label is sr-only, make it sensible. */
  label?: string
}> = ({
  onChange,
  max = 100000000,
  min = 0,
  value,
  className,
  buttonClassName,
  inputClassName,
  disabled,
  placeholder,
  minusButtonClassName,
  plusButtonClassName,
  label = 'Select number',
}) => {
  if (min < 0) {
    console.error('number picker does not support negative numbers')
  }
  const labelId = useId()
  const clamp = (n: number) => Math.min(Math.max(Math.floor(n), min), max)
  // Keep local track of input state to allow some edge cases (like
  // zero), but most updates will happen through the `useUpdateEffect`.
  const [val, setVal] = useState(clamp(value))

  const _onChange = (newVal: number) => {
    const clampedNewVal = clamp(newVal)
    if (clampedNewVal !== val) {
      onChange(clampedNewVal)
      setVal(clampedNewVal)
    }
  }

  // If the value gets changed from above, then we update to match it.
  useUpdateEffect(() => {
    setVal(value)
  }, [value])

  return (
    <div data-cy="number-picker" className={className ?? 'space-x-4'}>
      <label className="sr-only" htmlFor={labelId}>
        {label}
      </label>
      <button
        disabled={val <= min || disabled}
        className={classNames(buttonClassName, minusButtonClassName ?? 'rounded-l')}
        onClick={() => _onChange(val - 1)}
      >
        &minus;
      </button>
      <input
        id={labelId}
        data-cy="number-picker-input"
        onBlur={() => _onChange(val)}
        className={classNames(inputClassName, 'number-input-no-spin')}
        type="text"
        inputMode="numeric"
        disabled={disabled}
        pattern="[0-9]*"
        max={max}
        placeholder={placeholder}
        value={val}
        onChange={(e) => {
          const n = +e.target.value
          if (!isNaN(n)) {
            const val = Math.min(Math.floor(n), max)
            if (val < 0) {
              return
            }
            setVal(val)
            // Do not propagate zeroes above, they only exist in the
            // number picker to allow for `{backspace}` to work for nice
            // UX.
            if (val >= min) {
              _onChange(val)
            }
          }
        }}
      />
      <button
        disabled={disabled || val >= max}
        className={classNames(buttonClassName, plusButtonClassName ?? 'rounded-r')}
        onClick={() => _onChange(val + 1)}
      >
        +
      </button>
    </div>
  )
}

export default NumberPicker
